import { useState, useMemo, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  V2Card,
  V2CardHeader,
  V2Button,
  V2Badge,
  V2Kpi,
  V2MetricStrip,
  Alert,
  LoadingState,
  StatePanel,
  type V2BadgeTone,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import type { SubscriptionResponse } from '../../lib/types/subscriptionSecurity.js';
import { upgradeTenantSubscription } from '../../lib/publicApi.js';

type PricingInterval = '1_month' | '3_months' | '6_months' | '12_months';

interface IntervalOption {
  readonly key: PricingInterval;
  readonly label: string;
  readonly months: number;
  readonly discountPercent: number;
  readonly tag?: string;
}

const INTERVALS: readonly IntervalOption[] = [
  { key: '1_month', label: '۱ ماهه', months: 1, discountPercent: 0 },
  { key: '3_months', label: '۳ ماهه', months: 3, discountPercent: 5, tag: '۵٪ تخفیف' },
  { key: '6_months', label: '۶ ماهه', months: 6, discountPercent: 10, tag: '۱۰٪ تخفیف' },
  { key: '12_months', label: 'یک‌ساله', months: 12, discountPercent: 16.67, tag: '۲ ماه رایگان' },
];

const BASE_SEATS = 10;
const BASE_PRICE_TOMAN = 12_000_000;
const EXTRA_SEAT_PRICE_TOMAN = 500_000;

function formatNumberFa(num: number): string {
  return num.toLocaleString('fa-IR');
}

/** `statusBadgeKind` is the old `Badge`'s tone vocabulary
 * (success|warning|danger|info|default) — `V2Badge`'s is
 * success|warn|danger|info|neutral|brand. Only the two labels differ. */
function v2BadgeTone(kind: 'success' | 'warning' | 'danger' | 'info' | 'default'): V2BadgeTone {
  if (kind === 'warning') return 'warn';
  if (kind === 'default') return 'neutral';
  return kind;
}

/** Same pill-chip look `ModuleAnswersStep.tsx`'s `chip-single` fields and
 * `AccountPage.tsx`'s brand-color swatches use — kept local since neither
 * of those is a shared component, just a shared style convention. */
function chipStyle(active: boolean): React.CSSProperties {
  return {
    padding: '10px 12px',
    borderRadius: 'var(--sk-r-lg)',
    border: `1.5px solid ${active ? 'var(--sk-brand-500)' : 'var(--sk-border)'}`,
    background: active
      ? 'color-mix(in oklch, var(--sk-brand-500), transparent 94%)'
      : 'var(--sk-bg-canvas)',
    color: active ? 'var(--sk-brand-600)' : 'var(--sk-fg)',
    cursor: 'pointer',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
  };
}

/**
 * UI_REFERENCE_SOURCE:
 * section: Sakhtar Pilot P0.html §2.3 "SUBSCRIPTION · 1000px" covers the
 *   plan/seat/trial **status** half of this page only (طرح فعال، دورهٔ
 *   صورت‌حساب، مصرف صندلی سه‌حالته، دورهٔ آزمایشی با آستانه‌های رنگی) —
 *   confirmed by rendering the file directly, 2026-09-20. That half is a
 *   reasonable match: real plan name/status badge, real seat used/limit,
 *   real trial-remaining-days.
 * status: no design authority for the "ارتقای پلن و تمدید اشتراک" upgrade/
 *   calculator card below — the board's own §2.3 frame is explicitly
 *   captioned "PROVIDER-SAFE — بدون نام درگاه" and shows only "انتخاب
 *   طرح"/"تماس با فروش" buttons, no calculator, no per-interval discount
 *   grid, no gateway branding — the opposite of this card's gateway-naming
 *   ("پرداخت آنلاین با زرین‌پال") implementation. Same `PR #166`
 *   zero-citation gap as `start/CheckoutPage.tsx` (see that file's own doc
 *   comment and `docs/design-system/PRODUCTION_READINESS_AUDIT.md`) —
 *   needs a real PO/Design Lead decision, not fabricated here. This pass
 *   only brings the markup onto the same V2 kit + `var(--sk-*)` tokens
 *   every other authenticated panel page already uses (it was still on
 *   the retired `PageHeader`/`Card`/`StatCard`/`Badge`/`Button` set with
 *   raw hex colors) — reported live as visually inconsistent with the
 *   rest of the app. No pricing/calculation logic changed.
 */
export function SubscriptionPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const paymentSuccess = searchParams.get('payment') === 'success';
  const refId = searchParams.get('refId');

  const [usersCount, setUsersCount] = useState<number>(10);
  const [selectedInterval, setSelectedInterval] = useState<PricingInterval>('12_months');
  const [upgrading, setUpgrading] = useState<boolean>(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  const result = useApiGet<SubscriptionResponse>(
    slug ? `${tenantBasePath(slug)}/api/v1/admin/subscription` : null,
    [slug],
  );

  const pricing = useMemo(() => {
    const extraSeats = Math.max(0, usersCount - BASE_SEATS);
    const monthlyExtra = extraSeats * EXTRA_SEAT_PRICE_TOMAN;
    const monthlyTotal = BASE_PRICE_TOMAN + monthlyExtra;

    const intervalObj: IntervalOption =
      INTERVALS.find((i) => i.key === selectedInterval) ?? INTERVALS[3]!;
    const rawTotal = monthlyTotal * intervalObj.months;

    let finalTotal: number;
    let discountToman: number;

    if (selectedInterval === '12_months') {
      finalTotal = monthlyTotal * 10; // 2 months free
      discountToman = rawTotal - finalTotal;
    } else if (intervalObj.discountPercent > 0) {
      discountToman = Math.round((rawTotal * intervalObj.discountPercent) / 100);
      finalTotal = rawTotal - discountToman;
    } else {
      discountToman = 0;
      finalTotal = rawTotal;
    }

    const effectiveMonthly = Math.round(finalTotal / intervalObj.months);

    return {
      usersCount,
      extraSeats,
      monthlyTotal,
      rawTotal,
      discountToman,
      finalTotal,
      effectiveMonthly,
      intervalObj,
    };
  }, [usersCount, selectedInterval]);

  const handleUpgrade = async () => {
    if (!slug) return;
    try {
      setUpgrading(true);
      setUpgradeError(null);
      const res = await upgradeTenantSubscription(slug, {
        usersCount,
        interval: selectedInterval,
      });
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl;
      }
    } catch (err: unknown) {
      setUpgrading(false);
      const msg = (err as Error).message || '';
      if (msg.includes('Organization name') || msg.includes('owner name')) {
        setUpgradeError(
          'اطلاعات اشتراک سازمان معتبر نیست. لطفاً صفحه را تازه‌سازی کرده و مجدداً تلاش کنید.',
        );
      } else {
        setUpgradeError(
          msg ||
            'خطا در اتصال به درگاه پرداخت. لطفاً وضعیت اینترنت را بررسی کرده یا مجدداً تلاش کنید.',
        );
      }
    }
  };

  if (!slug) return null;

  if (result.status === 'loading') {
    return (
      <PageWrap>
        <LoadingState />
      </PageWrap>
    );
  }

  if (result.status === 'error' || result.data.phase !== 'ready' || !result.data.subscription) {
    const permissionDenied = result.status === 'ready' && result.data.phase === 'permission_denied';
    return (
      <PageWrap>
        <StatePanel
          variant={permissionDenied ? 'permission_denied' : 'network_error'}
          title={permissionDenied ? 'دسترسی به اشتراک ندارید' : 'اشتراک قابل نمایش نیست'}
          description={permissionDenied ? undefined : 'دوباره تلاش کنید.'}
        />
      </PageWrap>
    );
  }

  const sub = result.data.subscription;

  return (
    <PageWrap>
      {paymentSuccess ? (
        <Alert tone="success" title="پرداخت شما با موفقیت انجام شد!">
          اشتراک و تعداد کاربران سازمان شما با موفقیت ارتقا یافت.
          {refId ? ` کد پیگیری شاپرک: ${refId}` : ''}
        </Alert>
      ) : null}

      <V2MetricStrip>
        <V2Kpi
          label="طرح فعلی"
          value={
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15 }}>
              {sub.planName}
              <V2Badge tone={v2BadgeTone(sub.statusBadgeKind)}>{sub.statusLabel}</V2Badge>
            </span>
          }
        />
        <V2Kpi label="صندلی مصرف‌شده" value={sub.seat.usedLabel} />
        <V2Kpi label="سقف صندلی فعلی" value={sub.seat.limitLabel} />
        {sub.trial ? (
          <V2Kpi label="روزهای باقی‌مانده آزمایشی" value={String(sub.trial.remainingDays)} />
        ) : null}
        {sub.paid ? (
          <V2Kpi label="پایان دوره جاری" value={sub.paid.currentPeriodEndLabel} />
        ) : null}
      </V2MetricStrip>

      <V2Card>
        <V2CardHeader title="ارتقای پلن و تمدید اشتراک (پرداخت آنلاین)" />
        <div style={{ padding: '0 20px 20px', display: 'flex', flexDirection: 'column', gap: 22 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              تعداد کاربران مورد نیاز سازمان
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <V2Button
                variant="secondary"
                size="sm"
                onClick={() => setUsersCount((prev) => Math.max(1, prev - 1))}
                disabled={usersCount <= 1}
              >
                −
              </V2Button>
              <div
                style={{
                  minWidth: 90,
                  textAlign: 'center',
                  fontSize: 22,
                  fontWeight: 700,
                  color: 'var(--sk-brand-600)',
                }}
                className="sk-num"
              >
                {formatNumberFa(usersCount)}{' '}
                <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--sk-fg-muted)' }}>
                  کاربر
                </span>
              </div>
              <V2Button
                variant="secondary"
                size="sm"
                onClick={() => setUsersCount((prev) => Math.min(30, prev + 1))}
                disabled={usersCount >= 30}
              >
                +
              </V2Button>
              <span style={{ fontSize: 12, color: 'var(--sk-fg-muted)' }}>
                (تا ۱۰ کاربر پلن پایه؛ مازاد: ۵۰۰٬۰۰۰ تومان/کاربر ماهانه)
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--sk-fg)' }}>
              دورهٔ پرداخت و تمدید
            </span>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 10,
              }}
            >
              {INTERVALS.map((inv) => {
                const active = selectedInterval === inv.key;
                return (
                  <button
                    key={inv.key}
                    type="button"
                    onClick={() => setSelectedInterval(inv.key)}
                    style={chipStyle(active)}
                  >
                    <span style={{ fontWeight: 600, fontSize: 14 }}>{inv.label}</span>
                    {inv.tag ? (
                      <span
                        style={{
                          fontSize: 10,
                          padding: '2px 8px',
                          borderRadius: 'var(--sk-r-full, 999px)',
                          background: active ? 'var(--sk-brand-500)' : 'var(--sk-success)',
                          color: 'var(--sk-fg-on-brand)',
                        }}
                      >
                        {inv.tag}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div
            style={{
              padding: 16,
              background: 'var(--sk-bg-canvas)',
              borderRadius: 'var(--sk-r-md)',
              border: '1px solid var(--sk-border)',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              fontSize: 13,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>تعرفه ماهانه برای {formatNumberFa(pricing.usersCount)} کاربر:</span>
              <span className="sk-num">
                <strong>{formatNumberFa(pricing.monthlyTotal)}</strong> تومان/ماه
              </span>
            </div>
            {pricing.discountToman > 0 ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  color: 'var(--sk-success)',
                }}
              >
                <span>تخفیف دوره ({pricing.intervalObj.label}):</span>
                <span className="sk-num">
                  <strong>- {formatNumberFa(pricing.discountToman)}</strong> تومان
                </span>
              </div>
            ) : null}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                paddingTop: 8,
                borderTop: '1px solid var(--sk-border-strong)',
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--sk-brand-600)',
              }}
            >
              <span>مبلغ نهایی قابل پرداخت ({pricing.intervalObj.label}):</span>
              <span className="sk-num">{formatNumberFa(pricing.finalTotal)} تومان</span>
            </div>
          </div>

          {upgradeError ? <Alert tone="danger">{upgradeError}</Alert> : null}

          <div>
            <V2Button
              variant="primary"
              icon="check"
              onClick={() => void handleUpgrade()}
              disabled={upgrading}
            >
              {upgrading
                ? 'در حال اتصال به درگاه...'
                : `پرداخت آنلاین با زرین‌پال (${formatNumberFa(pricing.finalTotal)} تومان)`}
            </V2Button>
          </div>
        </div>
      </V2Card>
    </PageWrap>
  );
}

function PageWrap({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        <h1 style={{ margin: 0, fontSize: 'var(--sk-fs-2xl)', fontWeight: 700 }}>اشتراک</h1>
        <div style={{ fontSize: 'var(--sk-fs-sm)', color: 'var(--sk-fg-muted)' }}>
          مدیریت صندلی‌ها، دورهٔ اشتراک و پرداخت آنلاین زرین‌پال
        </div>
      </div>
      {children}
    </div>
  );
}
